/**
 * Phase 4 slice 3 — Quality Dashboard (landing). KPI summary + tabs for
 * Recent NCRs and Cube Tests, plus a heatmap snapshot + quick actions.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileWarning, FlaskConical, Plus, MapPin, ArrowUpRight, AlertTriangle } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';
import { useUrlTab } from '../../lib/hooks/useUrlState';

const SEV_TONE = {
  low: 'bg-slate-100 text-slate-700 border-slate-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  critical: 'bg-red-50 text-red-700 border-red-200',
};
const SEVERITIES = ['low', 'medium', 'high', 'critical'];
import { formatDate as fmt } from '../../lib/format';

const acc = (m) => (m === true ? <span className="tag border bg-emerald-50 text-emerald-700 border-emerald-200">Pass</span>
  : m === false ? <span className="tag border bg-red-50 text-red-700 border-red-200">Fail</span>
    : <span className="tag border bg-slate-100 text-slate-500 border-slate-200">Pending</span>);

export default function QualityDashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [dash, setDash] = useState(null);
  const [heat, setHeat] = useState(null);
  const [tab, setTab] = useUrlTab('tab', 'ncrs');
  const [rem, setRem] = useState(null);          // NCR deadline reminders
  const [showRem, setShowRem] = useState(false); // expanded/collapsed state

  useEffect(() => { apiGet('/projects').then(setProjects).catch(() => toast.error('Failed to load projects')); }, []);
  useEffect(() => {
    const q = projectId ? `?project_id=${projectId}` : '';
    apiGet(`/quality/dashboard${q}`).then(setDash).catch(() => {});
    apiGet(`/quality/heatmap${q}`).then(setHeat).catch(() => {});
    apiGet(`/quality/ncr-reminders${q}`)
      .then(setRem)
      .catch(() => setRem(null));
  }, [projectId]);

  const obs = dash?.ncrs?.open_by_severity || {};
  const ct = dash?.cube_tests || {};

  return (
    <div>
      <PageHeader overline="Quality" title="Quality Management" subtitle="NCR / CAPA, concrete cube tests and quality hotspots"
        actions={(
          <div className="flex flex-wrap gap-2">
            <button onClick={() => navigate('/app/quality/ncrs/new')} className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-display font-bold uppercase tracking-wider rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e]" data-testid="qd-raise-ncr"><Plus size={14} /> NCR</button>
            <button onClick={() => navigate('/app/quality/cube-tests/new')} className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-display font-bold uppercase tracking-wider rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50" data-testid="qd-record-cube"><Plus size={14} /> Cube Test</button>
            <button onClick={() => navigate('/app/quality/heatmap')} className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-display font-bold uppercase tracking-wider rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50" data-testid="qd-heatmap"><MapPin size={14} /> Heatmap</button>
          </div>
        )}
      />

      <div className="mb-4">
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="qd-project-select" className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[260px]">
          <option value="">All projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
        </select>
      </div>

      {rem && rem.count > 0 && (
        <div className="mb-4 border border-amber-300 bg-amber-50 rounded-sm" data-testid="qd-ncr-reminders">
          <button onClick={() => setShowRem((s) => !s)} className="w-full flex items-center gap-2 px-3 py-2 text-left" data-testid="qd-ncr-reminders-toggle">
            <AlertTriangle size={14} className="text-amber-700 shrink-0" />
            <span className="text-sm font-display font-bold text-amber-800">{rem.count} NCR(s) approaching / past close-out deadline</span>
            <span className="ml-auto text-xs text-amber-700">{showRem ? 'Hide ▲' : 'Show ▼'}</span>
          </button>
          {showRem && (
            <div className="border-t border-amber-200 divide-y divide-amber-100">
              {rem.alerts.map((a) => (
                <div key={a.ncr_id} className="px-3 py-2 flex items-center gap-3 text-xs flex-wrap">
                  <span className="font-mono font-bold">{a.ncr_no}</span>
                  <span className="flex-1 truncate text-slate-600 min-w-[120px]">{a.description}</span>
                  <span className={a.is_overdue ? 'text-red-700 font-bold' : 'text-amber-700'}>{a.is_overdue ? 'OVERDUE' : `${a.elapsed_pct}% elapsed`}</span>
                  <span className="text-slate-500">due {a.due_date || '—'}</span>
                  <button onClick={() => navigate(`/app/quality/ncrs/${a.ncr_id}`)} className="text-[#3b4872] hover:underline">Open →</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="kpi-overline mb-3 flex items-center gap-2"><FileWarning size={12} /> Open NCRs by severity</div>
          <div className="grid grid-cols-4 gap-2">
            {SEVERITIES.map((s) => (
              <div key={s} className={`rounded-sm border p-2 text-center ${SEV_TONE[s]}`}>
                <div className="text-xl font-display font-extrabold tabular-nums">{obs?.[s] || 0}</div>
                <div className="text-[9px] uppercase tracking-wider font-bold">{s}</div>
              </div>
            ))}
          </div>
          <div className="text-xs text-slate-500 mt-2">{dash?.ncrs?.open || 0} open · {dash?.ncrs?.total || 0} total</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="kpi-overline mb-3 flex items-center gap-2"><FlaskConical size={12} /> Cube Tests</div>
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-2 text-center"><div className="text-xl font-display font-extrabold text-emerald-700">{ct.passed || 0}</div><div className="text-[9px] uppercase tracking-wider font-bold text-emerald-700">Pass</div></div>
            <div className="rounded-sm border border-red-200 bg-red-50 p-2 text-center"><div className="text-xl font-display font-extrabold text-red-700">{ct.failed || 0}</div><div className="text-[9px] uppercase tracking-wider font-bold text-red-700">Fail</div></div>
            <div className="rounded-sm border border-slate-200 p-2 text-center"><div className="text-xl font-display font-extrabold text-slate-500">{ct.pending || 0}</div><div className="text-[9px] uppercase tracking-wider font-bold text-slate-400">Pending</div></div>
          </div>
          <div className="text-xs text-slate-500 mt-2">{ct.total || 0} cube tests recorded</div>
        </div>

        <button onClick={() => navigate('/app/quality/heatmap')} className="bg-white border border-slate-200 rounded-sm p-4 text-left hover:border-[#3b4872]/50 transition-colors" data-testid="qd-hotspots-card">
          <div className="kpi-overline mb-3 flex items-center justify-between"><span className="flex items-center gap-2"><MapPin size={12} /> Top Hotspots</span><ArrowUpRight size={12} /></div>
          <div className="space-y-1">
            {(heat?.by_location || []).slice(0, 4).map((l, i) => (
              <div key={i} className="flex justify-between text-sm"><span className="text-slate-600 truncate max-w-[160px]">{l.key}</span><span className="font-display font-bold text-red-600">{l.count}</span></div>
            ))}
            {(!heat?.by_location || heat.by_location.length === 0) && <div className="text-xs text-slate-400">No NCRs yet.</div>}
          </div>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-3">
        {[['ncrs', 'Recent NCRs'], ['cubes', 'Recent Cube Tests']].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`qd-tab-${v}`}
            className={`px-4 py-2 text-sm font-display font-bold border-b-2 -mb-px transition-colors ${tab === v ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          {tab === 'ncrs' ? (
            <table className="w-full dense-table" data-testid="qd-ncr-table">
              <thead><tr><th>NCR No.</th><th>Title</th><th>Severity</th><th>Raised</th><th>Status</th></tr></thead>
              <tbody>
                {(dash?.recent_ncrs || []).length === 0 ? <tr><td colSpan={5} className="text-center text-sm text-slate-400 py-10">No NCRs.</td></tr>
                  : dash.recent_ncrs.map((n) => (
                    <tr key={n.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/app/quality/ncrs/${n.id}`)} data-testid={`qd-ncr-${n.id}`}>
                      <td className="text-xs font-display font-bold text-[#3b4872]">{n.ncr_no}</td>
                      <td className="text-[13px] font-display font-semibold max-w-[280px] truncate">{n.title}</td>
                      <td><span className={`tag border ${SEV_TONE[n.severity]}`}>{n.severity}</span></td>
                      <td className="text-xs text-slate-500">{fmt(n.raised_at)}</td>
                      <td className="text-xs text-slate-600">{(n.status || '').replace('_', ' ')}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full dense-table" data-testid="qd-cube-table">
              <thead><tr><th>Pour</th><th>Grade</th><th>Location</th><th className="text-center">28d Result</th><th>Status</th></tr></thead>
              <tbody>
                {(dash?.recent_cube_tests || []).length === 0 ? <tr><td colSpan={5} className="text-center text-sm text-slate-400 py-10">No cube tests.</td></tr>
                  : dash.recent_cube_tests.map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/app/quality/cube-tests/${c.id}`)} data-testid={`qd-cube-${c.id}`}>
                      <td className="text-xs text-slate-600">{fmt(c.pour_date)}</td>
                      <td className="font-display font-bold text-[13px]">{c.concrete_grade === 'Other' ? (c.concrete_grade_other || 'Other') : c.concrete_grade}</td>
                      <td className="text-[13px] max-w-[220px] truncate">{c.pour_location_on_site || '—'}</td>
                      <td className="text-center">{acc(c.acceptance_criteria_met)}</td>
                      <td className="text-xs text-slate-600">{(c.status || '').replace('_', ' ')}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
